import React from 'react';
import { SkeletonBlock, SkeletonRegion } from './BrandPresenceSkeleton';
import { BP_CARD_CLASSES } from './brandPresenceClasses';
import { KPI_CARD_ROW_CLASS_NAME } from './ContentItemKpiCards';

/** Health checks drawn while a page loads — the list the Health tab opens on. */
const CHECK_ROW_COUNT = 5;

function FigureCardSkeleton({ lines }: { lines: 'figure' | 'tags' }) {
  return (
    <div className={`${BP_CARD_CLASSES} h-full p-5`}>
      <SkeletonBlock className="h-3 w-28" />
      {lines === 'figure' ? (
        <>
          <SkeletonBlock className="mt-4 h-10 w-24 rounded-lg" />
          <SkeletonBlock className="mt-3 h-5 w-32 rounded-full" />
        </>
      ) : (
        <div className="mt-4 flex flex-wrap gap-1.5">
          <SkeletonBlock className="h-6 w-20 rounded-full" />
          <SkeletonBlock className="h-6 w-28 rounded-full" />
          <SkeletonBlock className="h-6 w-16 rounded-full" />
        </div>
      )}
    </div>
  );
}

/**
 * One page while its record is on the way: the header, the two rows of figure cards and the tabbed
 * card opened on its health checks — each shape where the real one lands.
 */
export function ContentItemDetailSkeleton() {
  return (
    <SkeletonRegion label="Loading this page" className="w-full h-full bg-slate-50">
      <div className="max-w-[1600px] mx-auto px-6 py-8 space-y-6">
        <div>
          <SkeletonBlock className="h-7 w-48 rounded-lg" />
          <SkeletonBlock className="mt-5 h-3 w-40" />
          <SkeletonBlock className="mt-3 h-8 w-[28rem] max-w-full rounded-lg" />
          <SkeletonBlock className="mt-3 h-3 w-72 max-w-full" />
        </div>
        <div className={KPI_CARD_ROW_CLASS_NAME}>
          {(['figure', 'figure', 'tags', 'tags'] as const).map((lines, index) => (
            <div key={index} className="col">
              <FigureCardSkeleton lines={lines} />
            </div>
          ))}
        </div>
        <div className={KPI_CARD_ROW_CLASS_NAME}>
          {(['figure', 'tags'] as const).map((lines, index) => (
            <div key={index} className="col">
              <FigureCardSkeleton lines={lines} />
            </div>
          ))}
        </div>
        <div className={BP_CARD_CLASSES}>
          <div className="flex gap-6 border-b border-slate-200/70 px-6 py-4">
            <SkeletonBlock className="h-4 w-16" />
            <SkeletonBlock className="h-4 w-16" />
            <SkeletonBlock className="h-4 w-16" />
            {/* The switch that shows each check's weight. */}
            <SkeletonBlock className="ml-auto h-4 w-24" />
          </div>
          {/* Where the table is narrow, a check's first detection and potential gain read under its name. */}
          <div className="@container divide-y divide-slate-100">
            {Array.from({ length: CHECK_ROW_COUNT }, (_unused, row) => (
              <div key={row} className="grid grid-cols-[1fr_7rem] items-center gap-6 px-6 py-4 @3xl:grid-cols-[1fr_8rem_10rem_8rem]">
                <SkeletonBlock className={`h-4 ${row % 2 ? 'w-1/2' : 'w-2/3'}`} />
                <SkeletonBlock className="h-4 w-12" />
                <SkeletonBlock className="hidden h-4 w-24 @3xl:block" />
                <SkeletonBlock className="ml-auto hidden h-4 w-16 @3xl:block" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </SkeletonRegion>
  );
}
